import { z } from 'zod';
import { allUserRoleSchema, USER_TYPE } from '../data.types.js';
import { BaseResponseSchema, dateSchema, emailSchema, firstNameSchema, integerIdSchema, lastNameSchema, passwordSchema, phoneNumberSchema, userTypeSchema, uuidSchema, } from './primitives.js';
export const UpdateOwnProfileRequestSchema = z.object({
    firstName: firstNameSchema.optional(),
    lastName: lastNameSchema.optional(),
    phone: phoneNumberSchema.optional(),
});
export const EditOwnProfileFormSchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    phone: phoneNumberSchema.optional(),
});
export const UpdateOwnProfileResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    data: z.object({
        user: z.object({
            id: integerIdSchema,
            email: emailSchema,
            firstName: firstNameSchema,
            lastName: lastNameSchema,
            phone: phoneNumberSchema.optional(),
            avatarUrl: z.string().url().optional(),
            role: allUserRoleSchema,
            isActive: z.boolean(),
            lastLoginAt: dateSchema.nullable(),
        }),
    }),
});
export const UploadOwnAvatarResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    data: z.object({
        avatarUrl: z.string().url(),
        avatarObjectKey: z.string().min(1),
    }),
});
function isBrowserFile(value) {
    const FileCtor = globalThis.File;
    return typeof FileCtor === 'function' && value instanceof FileCtor;
}
/** FormData body for POST /unified-auth/me/avatar. */
export const UploadOwnAvatarFormSchema = z.object({
    file: z.custom(isBrowserFile),
});
export const PasswordPolicySchema = z.object({
    minimumLength: z.number().int().min(6).max(99),
    requireUppercase: z.boolean(),
    requireLowercase: z.boolean(),
    requireNumbers: z.boolean(),
    requireSymbols: z.boolean(),
    temporaryPasswordValidityDays: z.number().int().min(1).max(365),
});
export const PasswordPolicyResponseSchema = z.object({
    success: z.boolean(),
    data: PasswordPolicySchema,
});
export const MeUserResponseSchema = z.object({
    id: integerIdSchema,
    email: emailSchema,
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: phoneNumberSchema.optional(),
    avatarUrl: z.string().url().optional(),
    role: allUserRoleSchema,
    tenantId: uuidSchema,
    isActive: z.boolean(),
    lastLoginAt: dateSchema.optional(),
});
export const MeTenantResponseSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    subdomain: z.string(),
    isActive: z.boolean(),
});
export const MeResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        user: MeUserResponseSchema,
        tenant: MeTenantResponseSchema.nullable(),
    }),
});
// Cognito public auth (insight-api cognito-auth.routes.ts)
export const CognitoRegisterRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema.min(8),
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    phone: phoneNumberSchema.optional(),
    userType: z.enum([USER_TYPE.provider, USER_TYPE.tenant]),
    tenantId: uuidSchema.optional(),
    role: allUserRoleSchema,
});
export const CognitoCodeDeliveryDetailsSchema = z
    .object({
    Destination: z.string().optional(),
    DeliveryMedium: z.string().optional(),
    AttributeName: z.string().optional(),
})
    .passthrough();
export const CognitoRegisterResponseSchema = z.object({
    message: z.string(),
    user: z.object({
        id: integerIdSchema,
        email: emailSchema,
        firstName: firstNameSchema,
        lastName: lastNameSchema,
        userType: userTypeSchema,
        role: allUserRoleSchema,
        cognitoSub: z.string().min(1),
    }),
    confirmationRequired: z.boolean(),
    codeDeliveryDetails: CognitoCodeDeliveryDetailsSchema.optional(),
});
export const CognitoConfirmSignUpRequestSchema = z.object({
    email: emailSchema,
    confirmationCode: z.string().min(1),
});
export const CognitoMessageResponseSchema = z.object({
    message: z.string(),
});
export const CognitoRespondToAuthChallengeRequestSchema = z.object({
    email: emailSchema,
    newPassword: passwordSchema.min(8),
    session: z.string().min(1),
});
export const CognitoAuthTokensSchema = z.object({
    accessToken: z.string(),
    idToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
    tokenType: z.string(),
});
export const CognitoChallengeUserSchema = z.object({
    id: integerIdSchema,
    email: emailSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    userType: userTypeSchema,
    tenantId: uuidSchema.nullable().optional(),
    role: allUserRoleSchema,
    cognitoSub: z.string().min(1),
    hasTemporaryPassword: z.boolean(),
});
export const CognitoRespondToAuthChallengeResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tokens: CognitoAuthTokensSchema,
        user: CognitoChallengeUserSchema,
    }),
});
export const CognitoRefreshTokenRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
export const CognitoRefreshTokenResponseSchema = z.object({
    message: z.string(),
    tokens: CognitoAuthTokensSchema.omit({ refreshToken: true }),
});
export const CognitoForgotPasswordRequestSchema = z.object({
    email: emailSchema,
});
export const CognitoForgotPasswordResponseSchema = z.object({
    message: z.string(),
    codeDeliveryDetails: CognitoCodeDeliveryDetailsSchema.optional(),
});
export const CognitoResetPasswordRequestSchema = z.object({
    email: emailSchema,
    confirmationCode: z.string().min(1),
    newPassword: passwordSchema.min(8),
});
export const CognitoSyncUserRequestSchema = z.object({
    email: emailSchema,
    cognitoSub: z.string().min(1),
    accessToken: z.string().optional(),
});
export const CognitoSyncUserResponseSchema = z.object({
    success: z.boolean(),
    user: z.object({
        id: integerIdSchema,
        email: emailSchema,
        firstName: firstNameSchema,
        lastName: lastNameSchema,
        phone: phoneNumberSchema.optional(),
        avatarUrl: z.string().url().optional(),
        userType: userTypeSchema,
        tenantId: uuidSchema,
        role: allUserRoleSchema,
        isActive: z.boolean(),
    }),
});
//# sourceMappingURL=auth.schemas.js.map